import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { EvalRadar, MonthLegend } from './DesignEvaluationRadar.js';
import { EVALUATION_AXES, EVALUATION_RADIUS_DOMAIN } from './designEvaluation.js';
import type { EvalSeriesPoint } from './designEvaluation.js';
import { colorVarOf, onDesignerColor } from './shared.js';
import type { DesignerId } from './shared.js';
import { useIdtEvaluationCommentSummary } from '../../../hooks/useIdtKpi.js';
import type { IdtCategory } from '../../../lib/api.js';

/** 種別pt 1 行ぶん (表示順固定)。 @graph-connects none */
export interface KarteCategoryPt {
  category: IdtCategory;
  pt: number;
}

interface KarteModalProps {
  id: DesignerId;
  /** 当月 ("YYYY-MM")。基本情報 (戦闘力 / 種別pt) の対象月 */
  month: string;
  /** 当月戦闘力 (Pt/日)。null 可 (snapshot/fallback 共に未設定) */
  combatPower: number | null;
  /** 当月の種別pt内訳 (デザイン → 情報設計 → チェック → マージ の固定順) */
  categoryPt: KarteCategoryPt[];
  /** 前月の個人評価 (貢献度 3 軸)。null なら前月データなし表示 */
  evalSelf: EvalSeriesPoint | null;
  /** 前月の全体評価 (灰ベースライン)。null 可 */
  evalOverall: EvalSeriesPoint | null;
  /** レーダー / コメント要約の対象月 ("YYYY-MM"。= 前月) */
  evalMonth: string;
  /** レーダー対象月の表示ラベル (例 "2026年5月") */
  evalMonthLabel: string;
  onClose: () => void;
}

/** 種別 → デザイナー主題色を薄める割合 (DonutBlock の濃淡と揃える)。 @graph-connects none */
const CATEGORY_TINT_PCT: Record<IdtCategory, number> = {
  デザイン: 100,
  情報設計: 70,
  デザインチェック: 45,
  マスターデザインマージ: 25,
};

/**
 * デザイナー個人の karte (閲覧専用) modal。当月の基本情報 (戦闘力 + 種別pt 占比) と、
 * 前月の定性評価レーダー (個人=主題色 vs 全体=灰) を大きく表示し、MyPage へ導線する。
 * 入力・保存は持たない。Esc / 背景 click で close する。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI ページの karte modal (閲覧専用)。当月戦闘力・種別pt占比・前月定性評価レーダー・MyPage 導線
 * @graph-connects EvalRadar [calls] 個人 vs 全体 の貢献度 3 軸レーダー描画
 * @graph-connects /idt-designer/$nickname [references] 対象デザイナーの MyPage へ導線 (Link)
 */
export function KarteModal({
  id,
  month,
  combatPower,
  categoryPt,
  evalSelf,
  evalOverall,
  evalMonth,
  evalMonthLabel,
  onClose,
}: KarteModalProps) {
  const { t } = useTranslation();
  const colorVar = colorVarOf(id);
  // 個人色ベタ塗りの上 (ヘッダ / MyPage ボタン) に載せる文字色。淡色の個人色は深色文字。
  const onColorText = onDesignerColor(id);
  // 前月分の Good/More 定性コメントの AI 要約 (karte を開いたときに発火)。
  const commentSummary = useIdtEvaluationCommentSummary(id, evalMonth);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const totalPt = categoryPt.reduce((s, c) => s + c.pt, 0);

  // body 直下に portal して、祖先の transform (animate-content-reveal 等) に影響されず
  // 常に viewport 中央へ固定表示する (karte カードの位置に依らない)。
  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[88vh] w-[min(1100px,92vw)] flex-col overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-2xl animate-content-reveal"
        onClick={(e) => {
          e.stopPropagation();
        }}
        role="dialog"
        aria-modal="true"
      >
        {/* ヘッダ (主題色) */}
        <div
          className="relative flex h-24 shrink-0 items-end p-5"
          style={{
            background: `linear-gradient(135deg, var(${colorVar}) 0%, color-mix(in srgb, var(${colorVar}) 55%, var(--color-bg-surface)) 100%)`,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="absolute top-3 right-3 flex h-7 w-7 items-center justify-center rounded-md text-[18px] leading-none transition-colors hover:bg-black/15"
            style={{ color: onColorText }}
          >
            ×
          </button>
          <div>
            <div
              className="text-[10px] uppercase tracking-wider opacity-80"
              style={{ color: onColorText }}
            >
              {t('idtKpi.karte.modalEyebrow')} · {month}
            </div>
            <h2 className="mt-0.5 text-xl font-bold leading-tight" style={{ color: onColorText }}>
              {id}
            </h2>
          </div>
        </div>

        {/* 本体: 左=基本情報 / 右=定性評価レーダー */}
        <div className="grid flex-1 grid-cols-1 gap-6 overflow-y-auto p-6 lg:grid-cols-[minmax(260px,340px)_1fr]">
          {/* 左上 基本情報 */}
          <div className="flex flex-col gap-5">
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('idtKpi.karte.basicInfo')}
              </h3>
              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
                <div className="text-[11px] text-[var(--color-text-muted)]">
                  {t('idtKpi.karte.combatPower')} (Pt/日)
                </div>
                <div
                  className="mt-0.5 font-mono text-3xl font-bold leading-none tabular-nums"
                  style={{ color: `var(${colorVar})` }}
                >
                  {combatPower != null ? combatPower.toFixed(1) : '—'}
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-baseline justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t('idtKpi.karte.categoryBreakdown')}
                </h3>
                <span className="font-mono text-xs tabular-nums text-[var(--color-text-muted)]">
                  {t('idtKpi.karte.total')} {totalPt.toFixed(1)} pt
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {categoryPt.map((c) => {
                  const pct = totalPt > 0 ? (c.pt / totalPt) * 100 : 0;
                  const swatch = `color-mix(in srgb, var(${colorVar}) ${String(CATEGORY_TINT_PCT[c.category])}%, white)`;
                  return (
                    <div key={c.category} className="flex items-center gap-2 text-sm">
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                        style={{ background: swatch }}
                        aria-hidden="true"
                      />
                      <span className="flex-1 truncate text-[var(--color-text-secondary)]">
                        {c.category}
                      </span>
                      <span className="font-mono tabular-nums text-[var(--color-text-primary)]">
                        {c.pt.toFixed(1)}
                      </span>
                      <span className="w-12 text-right font-mono text-xs tabular-nums text-[var(--color-text-muted)]">
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 左下: 評価コメント (Good/More) の AI 要約 (前月分) */}
            <div className="mt-auto">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('idtKpi.karte.commentSummaryTitle')} · {evalMonthLabel}
              </h3>
              {commentSummary.isLoading ? (
                <div className="space-y-2">
                  <div className="h-3 w-3/4 animate-pulse rounded bg-[var(--color-bg-elevated)]" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-[var(--color-bg-elevated)]" />
                </div>
              ) : commentSummary.data &&
                (commentSummary.data.good != null || commentSummary.data.more != null) ? (
                <div className="flex flex-col gap-3">
                  {commentSummary.data.good != null && (
                    <CommentBlock
                      label={t('idtKpi.karte.good')}
                      color="var(--color-success)"
                      text={commentSummary.data.good}
                    />
                  )}
                  {commentSummary.data.more != null && (
                    <CommentBlock
                      label={t('idtKpi.karte.more')}
                      color="var(--color-warning)"
                      text={commentSummary.data.more}
                    />
                  )}
                </div>
              ) : (
                <p className="text-xs text-[var(--color-text-muted)]">
                  {t('idtKpi.karte.noComment')}
                </p>
              )}
            </div>
          </div>

          {/* 右 定性評価レーダー (前月 · 個人 vs 全体) */}
          <div className="flex min-w-0 flex-col">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
              {t('idtKpi.karte.qualitativeEval')} · {evalMonthLabel}
            </h3>
            {evalSelf ? (
              <>
                <EvalRadar
                  axes={EVALUATION_AXES}
                  selected={evalSelf.evaluation}
                  prev={evalOverall?.evaluation ?? null}
                  height={420}
                  domain={EVALUATION_RADIUS_DOMAIN}
                  currentColor={`var(${colorVar})`}
                  selectedLabel={id}
                  prevLabel={evalOverall ? t('idtKpi.karte.overall') : null}
                  axisCounts={evalSelf.evaluationCounts}
                  axisLabelColor="var(--color-text-secondary)"
                />
                <MonthLegend
                  selectedLabel={id}
                  prevLabel={evalOverall ? t('idtKpi.karte.overall') : null}
                  currentColor={`var(${colorVar})`}
                />
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center py-16 text-sm text-[var(--color-text-muted)]">
                {t('idtKpi.karte.noEvalData')}
              </div>
            )}
          </div>
        </div>

        {/* 最下部: MyPage 導線 */}
        <div className="flex shrink-0 justify-end border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-6 py-3">
          <Link
            to="/idt-designer/$nickname"
            params={{ nickname: id }}
            className="rounded-md px-4 py-1.5 text-sm font-semibold transition-opacity hover:opacity-90"
            style={{
              background: `var(${colorVar})`,
              color: onColorText,
            }}
          >
            {t('idtKpi.karte.myPageLink')}
          </Link>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Good / More 要約 1 ブロック (色付きラベル + 本文)。 @graph-connects none */
function CommentBlock({ label, color, text }: { label: string; color: string; text: string }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
      <span
        className="mb-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
        style={{ background: `color-mix(in srgb, ${color} 18%, transparent)`, color }}
      >
        {label}
      </span>
      <p className="whitespace-pre-wrap text-xs leading-relaxed text-[var(--color-text-secondary)]">
        {text}
      </p>
    </div>
  );
}
